import { api } from "@/lib/api";
import { type FontDownloaderVariant, type FontWeight, getFontKey } from "@custodia/shared";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

const loadedFontKeys = new Set<string>();

export { type CatalogFont, FONT_CATEGORIES, type FontWeight } from "@custodia/shared";

/**
 * Persistent local-first font cache (Cache Storage). Works identically in the
 * three runtimes that share this bundle — browser/PWA and the Tauri webview on
 * desktop and mobile — so a font downloaded once survives a cold start AND loads
 * offline. We store the woff2 RESPONSE keyed by its URL and register the
 * FontFace from the cached bytes, instead of relying on the browser's HTTP cache
 * heuristics (which may evict or revalidate over the network).
 */
const FONT_CACHE = "fonts-v1";

/** A URL we can hand straight to the FontFace API (http(s), blob: or data:).*/
function isAbsoluteUrl(url: string | undefined): url is string {
  return typeof url === "string" && /^(https?:|blob:|data:)/u.test(url);
}

function cacheStorageAvailable(): boolean {
  return typeof caches !== "undefined";
}

/**
 * Fetches the font bytes for a URL, cache-first: serve from Cache Storage when
 * present (offline-capable), otherwise fetch from the network and persist the
 * response for next time. Falls back to a plain network fetch if Cache Storage
 * is unavailable.
 */
async function fetchFontBytes(url: string): Promise<ArrayBuffer> {
  if (cacheStorageAvailable()) {
    try {
      const cache = await caches.open(FONT_CACHE);
      const hit = await cache.match(url);
      if (hit?.ok) {
        return await hit.arrayBuffer();
      }
      const res = await fetch(url, { mode: "cors" });
      if (res.ok) {
        await cache.put(url, res.clone());
        return await res.arrayBuffer();
      }
    } catch {
      // Cache Storage hiccup (private mode, quota) → fall through to direct fetch.
    }
  }
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) {
    throw new Error(`font fetch failed: ${url} (${res.status})`);
  }
  return await res.arrayBuffer();
}

/**
 * Loads a single FontFace, trying each candidate URL in order until one
 * resolves. Returns true on success. Tolerant of non-browser environments.
 * Bytes come from {@link fetchFontBytes} (Cache Storage first) so repeat loads
 * are instant and work offline.
 */
async function loadFontFaceFromCandidates(
  family: string,
  weight: FontWeight,
  candidateUrls: string[],
): Promise<boolean> {
  if (typeof FontFace === "undefined" || typeof document === "undefined") {
    return false;
  }
  for (const url of candidateUrls) {
    try {
      // Sequential by design: try each candidate in priority order and stop at
      // the first that loads (the next is only a fallback if this one 404s).
      // oxlint-disable-next-line no-await-in-loop
      const bytes = await fetchFontBytes(url);
      const face = new FontFace(family, bytes, { weight });
      // oxlint-disable-next-line no-await-in-loop
      await face.load();
      document.fonts.add(face);
      return true;
    } catch {
      // Try the next candidate (e.g. the CDN fallback when self-hosted 404s).
    }
  }
  return false;
}

export const FontDownloaderService = {
  isLoaded(fontKey: string): boolean {
    return loadedFontKeys.has(fontKey);
  },

  clearFamilyKeys(family: string): void {
    const safeFamily = family.replaceAll(/\s+/gu, "");
    const toDelete: string[] = [];
    for (const key of loadedFontKeys) {
      if (key.startsWith(`${safeFamily}-`)) {
        toDelete.push(key);
      }
    }
    for (const key of toDelete) {
      loadedFontKeys.delete(key);
    }

    if (typeof document !== "undefined") {
      const { fonts } = document;
      const stale: FontFace[] = [];
      for (const f of fonts) {
        if (f.family === family || toDelete.includes(f.family)) {
          stale.push(f);
        }
      }
      for (const f of stale) {
        fonts.delete(f);
      }
    }
  },

  async getDiskCacheStatus(
    _family: string,
    variants: FontDownloaderVariant[],
  ): Promise<"cached" | "partial" | "not-cached" | "not-applicable"> {
    if (!cacheStorageAvailable()) {
      return "not-applicable";
    }
    try {
      const cache = await caches.open(FONT_CACHE);
      const urls = variants.map((v) => v.url).filter(isAbsoluteUrl);
      if (urls.length === 0) {
        return "not-applicable";
      }
      const hits = await Promise.all(urls.map((url) => cache.match(url)));
      const cached = hits.filter((r) => r?.ok).length;
      if (cached === 0) {
        return "not-cached";
      }
      return cached === urls.length ? "cached" : "partial";
    } catch {
      return "not-applicable";
    }
  },

  async downloadAndLoad(
    family: string,
    variants: FontDownloaderVariant[],
    _db?: AbstractPowerSyncDatabase,
  ): Promise<void> {
    if (variants.length === 0) {
      return;
    }

    const needed = variants.filter((v) => !loadedFontKeys.has(getFontKey(family, v.weight)));
    if (needed.length === 0) {
      return;
    }

    // Catalog variants already carry stable, CORS-enabled CDN URLs (Fontsource),
    // so we only ask the backend for self-hosted storage URLs when a variant is
    // missing a usable URL. This avoids a wasteful (and potentially 404ing)
    // round-trip on every font load.
    let urlMap = new Map<string, string>();
    const needsResolution = needed.some((v) => !isAbsoluteUrl(v.url));
    if (needsResolution) {
      try {
        const { data, error: apiError } = await api.POST("/api/font/getDownloadUrls", {
          body: {
            family,
            weights: needed.map((v) => v.weight),
            format: "woff2",
          },
        });

        if (apiError) {
          throw new Error((apiError as { message?: string }).message ?? "Unknown API error");
        }

        if (Array.isArray(data.urls)) {
          urlMap = new Map(data.urls.map((u) => [u.weight, u.url]));
        }
      } catch (error) {
        console.warn(
          `[FontDownloader] URL resolution failed for ${family} - using variant fallbacks`,
          error,
        );
      }
    }

    const loadPromises = needed.map(async (variant) => {
      const { weight } = variant;
      const fontKey = getFontKey(family, weight);

      // Prefer the variant's own CDN URL; fall back to the resolved storage URL
      // (and vice-versa) so a single failing source never blocks the font.
      const candidateUrls = [variant.url, urlMap.get(weight)].filter(isAbsoluteUrl);
      const uniqueCandidates = [...new Set(candidateUrls)];

      if (uniqueCandidates.length === 0) {
        console.warn(`[FontDownloader] No valid URL found for ${fontKey}`);
        return;
      }

      const loaded = await loadFontFaceFromCandidates(family, weight, uniqueCandidates);
      if (loaded) {
        loadedFontKeys.add(fontKey);
      } else {
        console.warn(`[FontDownloader] Failed to load font ${fontKey} from all candidates`);
      }
    });

    await Promise.all(loadPromises);
  },
};
